import { EFFECT_KIND, TARGET_KIND } from '../data/disasters.js';
// Advanced lookup is a superset of the Classic plants (same names)
// plus the storage plants, so it names targets in both modes.
import { ADVANCED_PLANT_BY_ID as PLANT_BY_ID } from '../data/advancedCapacity.js';
import { ADVANCED_DISASTER_EFFECTS } from '../data/advancedDisasters.js';
import { FAMILY_LABEL } from '../data/families.js';
import { demandAddAmountForEffect } from '../engine/disasters.js';
import { firmFactorFromGenerationFactor } from '../engine/advancedCapacity.js';

function describeOutputFactor(factor) {
  if (factor === 0) return 'Output offline';
  return `Output reduced by ${Math.round((1 - factor) * 100)}%`;
}

function describeFirmOnlyFactor(factor) {
  if (factor === 0) return 'Firm capacity offline';
  return `Firm capacity reduced by ${Math.round((1 - factor) * 100)}%`;
}

function describePlantSpecificOutput(effect) {
  if (!effect.factorsByPlantId) return null;
  return Object.entries(effect.factorsByPlantId)
    .map(([id, factor]) => {
      const plant = PLANT_BY_ID[id]?.name ?? id;
      return `${plant}: ${describeOutputFactor(factor)}`;
    })
    .join('; ');
}

function describeDemandAdd(effect, year) {
  if (effect.amountByYear && year) {
    return `Demand +${demandAddAmountForEffect(effect, year)} TWh this year`;
  }
  if (effect.amountByYear) {
    return Object.entries(effect.amountByYear)
      .map(([y, amount]) => `Year ${y} +${amount} TWh`)
      .join('; ');
  }
  return `Demand +${effect.amount} TWh this year`;
}

function describeOutputEffect(disaster) {
  if (disaster.twoTierSeverity && !disaster.severityRoll) {
    return ['mild', 'severe']
      .map((tier) => {
        const effect = disaster.severity[tier];
        const description =
          describePlantSpecificOutput(effect) ?? describeOutputFactor(effect.factor);
        return `${tier === 'mild' ? 'Mild' : 'Severe'}: ${description}`;
      })
      .join('; ');
  }
  return (
    describePlantSpecificOutput(disaster.effect) ??
    describeOutputFactor(disaster.effect.factor)
  );
}

// Advanced Mode: demand disasters can also raise peak
// demand (section 16.6); surface it alongside the energy line.
function describePeakDemandAdd(disaster, year) {
  const peakByYear = ADVANCED_DISASTER_EFFECTS[disaster.id]?.peakDemandAddByYear;
  if (!peakByYear) return null;
  if (year) {
    const amount = peakByYear[year] ?? 0;
    return amount > 0 ? `peak demand +${amount} GW` : null;
  }
  return Object.entries(peakByYear)
    .map(([y, amount]) => `Year ${y} peak +${amount} GW`)
    .join('; ');
}

function advancedFirmFactor(generationFactor, advancedEffect) {
  if (advancedEffect.firmUsesGenerationFactor) return generationFactor;
  return (
    advancedEffect.firmCapacityFactor ??
    firmFactorFromGenerationFactor(generationFactor)
  );
}

function describeAdvancedCapacityEffects(disaster, year) {
  const advancedEffect =
    disaster.advanced ?? ADVANCED_DISASTER_EFFECTS[disaster.id] ?? {};
  const clauses = [];

  if (
    disaster.effect.kind === EFFECT_KIND.OUTPUT_MULTIPLIER &&
    advancedEffect.affectsFirmCapacity !== false &&
    advancedEffect.affectsGeneration !== false
  ) {
    if (disaster.effect.factorsByPlantId) {
      clauses.push(
        Object.entries(disaster.effect.factorsByPlantId)
          .map(([id, factor]) => {
            const plant = PLANT_BY_ID[id]?.name ?? id;
            return `${plant}: ${describeFirmOnlyFactor(
              advancedFirmFactor(factor, advancedEffect)
            )}`;
          })
          .join('; ')
      );
    } else if (disaster.twoTierSeverity && !disaster.severityRoll) {
      clauses.push(
        ['mild', 'severe']
          .map((tier) => {
            const factor = advancedFirmFactor(
              disaster.severity[tier].factor,
              advancedEffect
            );
            return `${tier === 'mild' ? 'Mild' : 'Severe'}: ${describeFirmOnlyFactor(factor)}`;
          })
          .join('; ')
      );
    } else {
      clauses.push(
        describeFirmOnlyFactor(
          advancedFirmFactor(disaster.effect.factor, advancedEffect)
        )
      );
    }
  }

  const derates = advancedEffect.firmDerate
    ? Array.isArray(advancedEffect.firmDerate)
      ? advancedEffect.firmDerate
      : [advancedEffect.firmDerate]
    : [];
  for (const derate of derates) {
    const families = (derate.families ?? [])
      .map((family) => FAMILY_LABEL[family] ?? family)
      .join(', ');
    clauses.push(`${families}: ${describeFirmOnlyFactor(derate.factor)}`);
  }

  const peakDemand = describePeakDemandAdd(disaster, year);
  if (peakDemand) clauses.push(peakDemand);
  return clauses.join('; ');
}

function describeEffect(disaster, year, advanced) {
  const e = disaster.effect;
  switch (e.kind) {
    case EFFECT_KIND.OUTPUT_MULTIPLIER: {
      // Advanced Mode: some cards touch only the firm capacity
      // channel (e.g. Storage Facility Fault) — describing them as
      // an output hit would be wrong, storage generates nothing.
      if (advanced) {
        const adv = ADVANCED_DISASTER_EFFECTS[disaster.id] ?? {};
        if (
          adv.affectsGeneration === false &&
          adv.affectsFirmCapacity !== false
        ) {
          return (
            describeFirmOnlyFactor(adv.firmCapacityFactor ?? e.factor) +
            (e.persistent ? ', permanently' : '')
          );
        }
      }
      return describeOutputEffect(disaster) +
        (e.persistent ? ', permanently' : '');
    }
    case EFFECT_KIND.OPEX_MULTIPLIER:
      return `Fossil opex increases to ${Math.round(e.factor * 100)}%`;
    case EFFECT_KIND.DEMAND_ADD: {
      return describeDemandAdd(e, year);
    }
    case EFFECT_KIND.SUBSIDY_REMOVE:
      return 'Fossil fuel subsidy ends, permanently';
    default:
      return '';
  }
}

function describeTarget(disaster) {
  const t = disaster.target;
  switch (t.kind) {
    case TARGET_KIND.PLANTS:
      return t.ids.map((id) => PLANT_BY_ID[id]?.name ?? id).join(', ');
    case TARGET_KIND.FAMILY:
      return `${FAMILY_LABEL[t.family]} family`;
    case TARGET_KIND.ALL_VARIABLE:
      return 'Every variable plant';
    case TARGET_KIND.LARGEST_NON_NUCLEAR_FAMILY:
      return 'Largest non-nuclear family';
    case TARGET_KIND.LARGEST_FAMILY:
      return 'Largest family';
    case TARGET_KIND.PLAYER:
      return '';
    default:
      return '';
  }
}

function severityLabel(disaster) {
  if (disaster.severityRoll === 'mild') return 'Mild';
  if (disaster.severityRoll === 'severe') return 'Severe';
  return null;
}

function disasterImageSrc(disaster) {
  if (disaster.imageSrc) {
    return disaster.imageSrc;
  }
  if (disaster.id === 'supply-chain-bottleneck') {
    return '/disaster-images/supplychain.webp';
  }
  if (disaster.id === 'nuclear-safety-audit') {
    return '/disaster-images/smr-safety-audit.webp';
  }
  return `/disaster-images/${disaster.id}.webp`;
}

export function DisasterCard({
  disaster,
  testId,
  noEffect = false,
  year = null,
  advanced = false
}) {
  const severity = severityLabel(disaster);
  const target = describeTarget(disaster);
  const advancedCapacityEffects = advanced
    ? describeAdvancedCapacityEffects(disaster, year)
    : '';

  return (
    <article
      className={
        `disaster-card disaster-card-${disaster.tier}` +
        (noEffect ? ' disaster-card-no-effect' : '')
      }
      data-testid={testId}
    >
      <img
        className="disaster-card-image"
        src={disasterImageSrc(disaster)}
        alt=""
        loading="eager"
        decoding="async"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
      {noEffect ? (
        <span className="disaster-card-no-effect-label">No effect</span>
      ) : null}
      <h3 className="disaster-card-name">{disaster.name}</h3>
      <p className="disaster-card-description">{disaster.description}</p>
      <dl className="disaster-card-stats">
        <div>
          <dt>Effect</dt>
          <dd>{describeEffect(disaster, year, advanced)}</dd>
        </div>
        {advancedCapacityEffects ? (
          <div>
            <dt>Firm and peak</dt>
            <dd>{advancedCapacityEffects}</dd>
          </div>
        ) : null}
        {severity ? (
          <div>
            <dt>Severity</dt>
            <dd>{severity}</dd>
          </div>
        ) : null}
        {target ? (
          <div>
            <dt>Target</dt>
            <dd>{target}</dd>
          </div>
        ) : null}
      </dl>
    </article>
  );
}
